/**
 * QC charts — Coding checks tab.
 *   Completeness heatmap (over × field) · Consistency flags
 * Every ball listed here opens in the QC page's edit dialog.
 */
import { useMemo, useState } from "react";
import { inOrder, pct0 } from "./qcData";
import { COMPLETENESS_FIELDS, consistencyFlags } from "./qcChecks";
import { Card, Empty, Toggle } from "./qcUi";

function BallChip({ b, onOpenBall, children }) {
  return (
    <button type="button" onClick={() => onOpenBall?.(b.entry)}
      title={onOpenBall ? "Open this ball to edit" : undefined}
      className="inline-flex items-center gap-1 rounded-md border border-border-default px-1.5 py-0.5 text-[10px] tabular-nums text-text-primary hover:border-emerald-500/70 hover:bg-emerald-600/10">
      <span className="font-semibold">{b.label}</span>
      <span className="text-text-secondary">inns {b.innings}</span>
      {children}
    </button>
  );
}

// ── 19. Completeness ─────────────────────────────────────────────────
function Completeness({ balls, onOpenBall }) {
  const [pick, setPick] = useState(null); // { innings, over, field }
  const data = useMemo(() => {
    const ordered = inOrder(balls);
    const totals = COMPLETENESS_FIELDS.map((f) => ({ ...f, need: 0, has: 0, missing: [] }));
    const rows = new Map();
    for (const b of ordered) {
      const key = `${b.innings}|${b.over}`;
      const row = rows.get(key) || { key, innings: b.innings, over: b.over, cells: COMPLETENESS_FIELDS.map(() => ({ need: 0, missing: [] })) };
      COMPLETENESS_FIELDS.forEach((f, i) => {
        if (!f.need(b)) return;
        row.cells[i].need += 1;
        totals[i].need += 1;
        if (f.has(b)) totals[i].has += 1;
        else {
          row.cells[i].missing.push(b);
          totals[i].missing.push(b);
        }
      });
      rows.set(key, row);
    }
    return { rows: [...rows.values()], totals };
  }, [balls]);

  if (!data.rows.length) return <Card wide title="Coding completeness"><Empty>No balls in this selection.</Empty></Card>;
  const allNeed = data.totals.reduce((t, f) => t + f.need, 0);
  const allHas = data.totals.reduce((t, f) => t + f.has, 0);
  const picked = pick
    ? (pick.over == null ? data.totals[pick.field].missing : data.rows.find((r) => r.innings === pick.innings && r.over === pick.over)?.cells[pick.field].missing) || []
    : [];

  return (
    <Card wide title="Coding completeness"
      subtitle={`${pct0(allHas, allNeed)} of the expected fields are coded · a field is only expected where it applies (no wagon pin on a leave, no stroke on a wide) · click a cell to list its balls`}>
      <div className="overflow-x-auto">
        <table className="text-[11px] border-separate" style={{ borderSpacing: 2 }}>
          <thead>
            <tr>
              <th className="text-left text-[9px] uppercase tracking-wider text-text-secondary font-normal pr-2">Over</th>
              {COMPLETENESS_FIELDS.map((f, i) => (
                <th key={f.key} className="px-1 pb-1 font-normal min-w-[64px]">
                  <button type="button" onClick={() => setPick({ field: i, over: null })}
                    className="text-[10px] text-text-secondary hover:text-text-primary">{f.label}</button>
                  <div className="mt-1 text-[10px] tabular-nums text-text-primary">{pct0(data.totals[i].has, data.totals[i].need)}</div>
                  <div className="h-1 rounded-full bg-bg-primary overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${data.totals[i].need ? (data.totals[i].has / data.totals[i].need) * 100 : 0}%` }} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {data.rows.map((r) => (
              <tr key={r.key}>
                <th className="text-left text-text-secondary font-normal pr-2 whitespace-nowrap">
                  <span className="text-[9px]">I{r.innings}</span> <span className="text-text-primary">{r.over}</span>
                </th>
                {r.cells.map((c, i) => {
                  const miss = c.missing.length;
                  const sel = pick && pick.innings === r.innings && pick.over === r.over && pick.field === i;
                  if (!c.need) return <td key={i} className="h-6 rounded-[3px]" style={{ background: "var(--qc-grid)", opacity: 0.4 }} />;
                  return (
                    <td key={i} className={`h-6 text-center rounded-[3px] cursor-pointer ${sel ? "ring-2 ring-emerald-400" : ""}`}
                      style={{ background: miss ? `rgba(245,158,11,${0.25 + 0.6 * (miss / c.need)})` : "rgba(16,185,129,0.14)" }}
                      title={miss ? `${miss} of ${c.need} balls missing ${COMPLETENESS_FIELDS[i].label}` : `All ${c.need} coded`}
                      onClick={() => setPick(miss ? { innings: r.innings, over: r.over, field: i } : null)}>
                      {miss ? <span className="font-semibold text-text-primary">{miss}</span> : <span className="text-emerald-500/80">✓</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex flex-wrap gap-4 text-[10px] text-text-secondary">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "rgba(16,185,129,0.14)" }} />✓ all coded</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "rgba(245,158,11,0.7)" }} />number = balls missing it</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "var(--qc-grid)", opacity: 0.4 }} />not expected</span>
      </div>
      {pick && (
        <div className="mt-3 rounded-lg border border-border-default p-2.5">
          <div className="text-[11px] text-text-primary mb-2">
            Missing <b>{COMPLETENESS_FIELDS[pick.field].label}</b>{pick.over != null ? ` · innings ${pick.innings}, over ${pick.over}` : " · whole selection"} — {picked.length} ball{picked.length === 1 ? "" : "s"}
            <button type="button" onClick={() => setPick(null)} className="ml-2 text-text-secondary hover:text-text-primary">✕ close</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {picked.slice(0, 120).map((b) => <BallChip key={b.i} b={b} onOpenBall={onOpenBall} />)}
            {picked.length > 120 && <span className="text-[10px] text-text-secondary">+{picked.length - 120} more</span>}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── 20. Consistency flags ────────────────────────────────────────────
const SEV = {
  error: { label: "Contradiction", icon: "✕", cls: "text-red-400 border-red-500/40 bg-red-500/10" },
  missing: { label: "Missing detail", icon: "!", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  check: { label: "Worth a look", icon: "?", cls: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
};

function Consistency({ balls, filtered, onOpenBall }) {
  const [sev, setSev] = useState("all");
  const flags = useMemo(() => consistencyFlags(balls, { checkOvers: !filtered }), [balls, filtered]);
  const counts = flags.reduce((c, f) => ({ ...c, [f.severity]: (c[f.severity] || 0) + 1 }), {});
  const shown = sev === "all" ? flags : flags.filter((f) => f.severity === sev);
  return (
    <Card wide title="Consistency checks"
      subtitle={`${flags.length} flag${flags.length === 1 ? "" : "s"} · codes on a ball that contradict each other or look unusual · click a ball to open it${filtered ? " · over ball-counts skipped while the table is filtered" : ""}`}
      controls={<Toggle label="Severity" value={sev} onChange={setSev} options={[
        { key: "all", label: `All ${flags.length}` },
        { key: "error", label: `Contradictions ${counts.error || 0}` },
        { key: "missing", label: `Missing ${counts.missing || 0}` },
        { key: "check", label: `Check ${counts.check || 0}` },
      ]} />}>
      {!flags.length ? (
        <p className="text-xs text-emerald-400 py-6 text-center">✓ No contradictions found in this selection.</p>
      ) : (
        <div className="max-h-[420px] overflow-y-auto pr-1">
          <table className="w-full text-[11px]">
            <tbody>
              {shown.map((f) => (
                <tr key={f.id} className="border-t border-border-default">
                  <td className="py-1.5 pr-2 w-[140px]">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[10px] ${SEV[f.severity].cls}`}>
                      <b>{SEV[f.severity].icon}</b>{SEV[f.severity].label}
                    </span>
                  </td>
                  <td className="pr-2 w-[120px]"><BallChip b={f.b} onOpenBall={onOpenBall} /></td>
                  <td className="text-text-primary">{f.text}</td>
                  <td className="text-text-secondary text-right whitespace-nowrap pl-2">{f.b.bowler} → {f.b.striker}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function CodingChecks({ balls, filtered, onOpenBall }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Consistency balls={balls} filtered={filtered} onOpenBall={onOpenBall} />
      <Completeness balls={balls} onOpenBall={onOpenBall} />
    </div>
  );
}
